import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Palette, Monitor, Globe, Zap, Target, Clock, Award, Sparkles, Layout } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { EnhancedStructuredData } from "@/components/EnhancedStructuredData";
import { SEOArticleStructure } from "@/components/SEOArticleStructure";
import { useSEOOptimization } from "@/hooks/useSEOOptimization";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const BrandingWebTheWoodlandsPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  
  useSEOOptimization();
  const canonicalUrl = generateCanonicalUrl('/branding-web-the-woodlands');

  const services = [
    {
      icon: Target,
      title: "Estrategia de Marca Completa",
      description: "Posicionamiento, propuesta de valor, personalidad, tonos y manual de marca."
    },
    {
      icon: Palette,
      title: "Diseño de Identidad Visual",
      description: "Logotipo, tipografía, paleta de colores para digital e impreso."
    },
    {
      icon: Monitor,
      title: "Diseño Web Responsive",
      description: "WordPress, Shopify, Webflow — mobile-first y optimizado para conversión."
    },
    {
      icon: Sparkles,
      title: "Optimización UX/CRO",
      description: "Landing pages, embudos, pruebas A/B e integración de conversión."
    },
    {
      icon: Globe,
      title: "Versión Bilingüe (ES/EN)",
      description: "Contenido y navegación adaptados al mercado hispano/inglés local."
    },
    {
      icon: Zap,
      title: "Integración SEO desde Diseño",
      description: "Estructura semántica, velocidad, schema y SEO on-page integrado."
    }
  ];

  const process = [
    {
      day: "Día 0-14",
      title: "Discovery + Taller de Marca",
      description: "Definimos identidad, audiencia, valores y creamos el sitemap web."
    },
    {
      day: "Día 15-45",
      title: "Diseño + Desarrollo",
      description: "Creamos identidad, wireframes y desarrollamos el sitio con contenido."
    },
    {
      day: "Día 46-75",
      title: "Pruebas UX/CRO",
      description: "Optimizamos móvil, integramos formularios, tracking y versión bilingüe."
    },
    {
      day: "Día 76-90",
      title: "Lanzamiento & Seguimiento",
      description: "Activamos sitio, SEO técnico, métricas y plan de mantenimiento."
    }
  ];

  const results = [
    "Mayor confianza y reconocimiento de marca en tu mercado local",
    "Más tráfico cualificado y usuarios que convierten con estructura optimizada",
    "Mejor posicionamiento diferencial frente a competidores con plantillas genéricas",
    "Escalabilidad internacional/hispana gracias a contenido bilingüe",
    "Feedback medible: menos rebote, más formularios/llamadas, mejor tiempo en sitio"
  ];

  const sectors = [
    "Restaurantes Bilingües",
    "Clínicas de Salud",
    "Despachos Legales",
    "Start-ups Tech",
    "E-commerce Locales",
    "Profesionales de Servicios (HVAC, Remodelación)"
  ];

  const plans = [
    {
      name: "Lanzamiento de Marca + Web Lite",
      duration: "8-10 semanas",
      description: "Base sólida para empezar a construir tu presencia digital profesional.",
      features: [
        "Identidad básica completa",
        "Sitio web hasta 5 páginas",
        "Versión bilingüe incluida",
        "Responsive design"
      ]
    },
    {
      name: "Marca Premium + Web Growth",
      duration: "10-12 semanas",
      description: "Solución integral para negocios que buscan destacar y convertir.",
      features: [
        "Identidad completa + manual",
        "Hasta 10 páginas optimizadas",
        "Blog integrado",
        "SEO básico + CRO"
      ]
    },
    {
      name: "Brand & Web Suite + Escalado",
      duration: "12+ semanas",
      description: "Solución enterprise con e-commerce y mantenimiento continuo.",
      features: [
        "Identidad premium multilanguage",
        "E-commerce / marketplaces",
        "Mantenimiento mensual",
        "Análisis y optimización continua"
      ]
    }
  ];

  const faqs = [
    {
      question: "¿Necesito ambos servicios, branding y web, al mismo tiempo?",
      answer: "Sí. Una marca sólida sin web convertidora o una web sin identidad fuerte difícilmente generan impacto. Lo ideal es que trabajen juntos para ofrecer resultados consistentes y profesionales."
    },
    {
      question: "¿Cuánto tiempo tarda el proyecto de branding + web?",
      answer: "Por lo general entre 8 y 12 semanas desde el taller de marca hasta el lanzamiento del sitio funcional. La fase de optimización continua puede extenderse según los objetivos específicos."
    },
    {
      question: "¿Por qué es importante que el sitio sea bilingüe?",
      answer: "En el área de The Woodlands y Texas en general, muchas audiencias hablan inglés y español. Un sitio bilingüe te permite comunicar tu valor a ambos segmentos y aumentar significativamente las conversiones."
    },
    {
      question: "¿Cómo se mide el éxito del diseño web?",
      answer: "Medimos mediante: velocidad de carga, tasa de rebote, tiempo en el sitio, formularios/leads generados, conversiones directas y sesiones móviles vs escritorio. Usamos Google Analytics y herramientas especializadas."
    }
  ];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Agencia de Branding & Diseño Web en The Woodlands | Marca que Vende – Luis ROC</title>
        <meta 
          name="description" 
          content="Construye una marca sólida y una web que convierte en The Woodlands. Branding bilingüe (ES/EN), diseño web responsive y orientado a resultados con Luis ROC." 
        />
        <meta name="keywords" content="branding the woodlands, diseño web the woodlands, agencia branding, diseño web bilingüe, identidad corporativa, diseño responsive" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Agencia de Branding & Diseño Web en The Woodlands | Luis ROC" />
        <meta property="og:description" content="Marca sólida y web que convierte. Branding bilingüe y diseño orientado a resultados." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Branding & Diseño Web The Woodlands | Luis ROC" />
        <meta name="twitter:description" content="Construye una marca que vende con diseño web optimizado." />
      </Helmet>

      <EnhancedStructuredData
        service={{
          name: "Branding & Diseño Web The Woodlands",
          description: "Servicios profesionales de branding, identidad corporativa y diseño web para empresas en The Woodlands, Texas",
          provider: "Luis ROC",
          areaServed: ["The Woodlands", "Houston", "Spring", "Conroe", "Montgomery County"],
        }}
        faqs={faqs}
        webpage={{
          name: "Branding y Diseño Web en The Woodlands",
          description: "Servicios profesionales de branding y diseño web en The Woodlands, TX. Marca e identidad que convierten.",
          url: canonicalUrl,
          keywords: ["branding the woodlands", "diseño web", "identidad corporativa", "web bilingüe"]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main>
          <SEOArticleStructure
            title="Branding y Diseño Web en The Woodlands para marcas que quieren crecer"
            description="Servicios profesionales de branding y diseño web en The Woodlands. Identidad sólida y sitios que convierten."
            author="Luis ROC"
            keywords={["branding the woodlands", "diseño web", "identidad corporativa"]}
            className="container mx-auto px-4 py-8"
          >
            {/* Breadcrumb Navigation */}
            <BreadcrumbNav 
              items={[
                { name: "Servicios", url: "/servicios" },
                { name: "Branding & Web The Woodlands", url: "/branding-web-the-woodlands" }
              ]}
              language="es"
            />

            {/* Hero Section */}
            <section className="py-16 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                Branding y Diseño Web en The Woodlands para marcas que quieren crecer
              </h1>
              
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  En Luis ROC creemos que una marca bien construida y un sitio web bien diseñado no son un lujo, sino un motor 
                  de crecimiento para tu negocio en The Woodlands y sus alrededores. Te ayudamos a definir tu identidad de marca 
                  (logo, paleta, voz, tono) y a crear un sitio web que atraiga, convenza y convierta. Nuestra propuesta bilingüe 
                  (inglés/español) y orientada a resultados te asegura que cada elemento —desde el diseño hasta la estructura web— 
                  esté pensado para maximizar conversaciones, generar confianza y escalar tu negocio. ¿Listo para que tu marca y tu 
                  web trabajen para ti?
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => window.location.href = '/contacto'}
                  className="text-lg px-8"
                >
                  Agenda tu reunión inicial gratuita de 30 minutos
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = '/casos-de-exito'}
                  className="text-lg px-8"
                >
                  Ver Casos de Éxito
                </Button>
              </div>
            </section>

            {/* Services Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Nuestros servicios de Branding & Diseño Web
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                        <Icon className="h-12 w-12 text-primary mb-4" />
                        <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                        <p className="text-muted-foreground">{service.description}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Process Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Nuestro proceso en 90 días
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Entregamos dossier de marca + manual + acceso al CMS + reporte de lanzamiento
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 relative">
                    <div className="absolute -top-4 left-6 bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-primary">{step.day}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* Results Section */}
            <section className="py-16 bg-primary/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Resultados que puedes esperar
                </h2>
                
                <div className="max-w-3xl mx-auto space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-background rounded-lg">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Sectors Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Sectores que servimos en The Woodlands
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Adaptamos el branding al nicho, al idioma y al mercado local
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                {sectors.map((sector, index) => (
                  <div 
                    key={index}
                    className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-medium hover:bg-accent/80 transition-colors"
                  >
                    {sector}
                  </div>
                ))}
              </div>
            </section>

            {/* Plans Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                  Planes y precios
                </h2>
                <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                  Solicita una propuesta personalizada según tus objetivos y audiencia
                </p>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {plans.map((plan, index) => (
                    <Card key={index} className="p-8 hover:shadow-xl transition-shadow">
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-primary font-semibold mb-4">{plan.duration}</p>
                      <p className="text-muted-foreground mb-6">{plan.description}</p>
                      
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className="w-full"
                        onClick={() => window.location.href = '/contacto'}
                      >
                        Solicitar Propuesta
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Preguntas Frecuentes
              </h2>
              
              <div className="max-w-3xl mx-auto space-y-6">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Construye tu Marca y Sitio Web que Vende
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                reunión inicial gratuita de branding y análisis de tu presencia digital actual
              </p>
              <Button 
                size="lg"
                variant="secondary"
                onClick={() => window.location.href = '/contacto'}
                className="text-lg px-8"
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
            </section>

            {/* Internal Links Section */}
            <section className="py-16">
              <h2 className="text-2xl font-bold mb-6 text-center">Recursos Relacionados</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">SEO The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Complementa tu nueva marca con SEO local potente
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/seo-the-woodlands'}>
                    Ver SEO Local →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Google Ads The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Acelera resultados con PPC después del lanzamiento
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/google-ads-the-woodlands'}>
                    Ver PPC →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Casos de Éxito</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Resultados reales de marcas transformadas
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/casos-de-exito'}>
                    Ver casos →
                  </Button>
                </Card>
              </div>
            </section>
          </SEOArticleStructure>
        </main>

        <KnowledgeConnections path="/branding-web-the-woodlands" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default BrandingWebTheWoodlandsPage;
